import "mapbox-gl/dist/mapbox-gl.css";
import mapboxgl, { LngLatLike } from "mapbox-gl";
import { useEffect, useRef, useState } from "react";

import routesData from "./PRTPATH_output.json";
import {
  getSelectedRoutes,
  isMobile,
  MAP_ROUTE_NAMES,
  MapRouteName,
  RouteState,
  TravelTimes,
} from "src/helpers/mapConstants";
import { createRoot } from "react-dom/client";
import RoutePopup from "./RoutePopup";
import MapInstructionsCard from "./MapInstructionsCard";
import { TravelTimesTable } from "./TravelTimesTable";
import { cn } from "src/lib/utils";
import TooltipContent from "../ui/TooltipContent";
import { LanguageContext } from "../languageContext";
import { useContext } from "react";
import AccessibleAddressInputs from "./AccessibleAddressInputs";

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN as string;

const mapCenter: LngLatLike = [-122.421608, 37.760105];
const mapZoom = 13.6;

export default function RouteTestingMap() {
  type Route = {
    [key in MapRouteName]?: {
      travel_time?: number | null;
      links?: string;
    };
  };

  type Routes = {
    [key: string]: {
      [key: string]: Route;
    };
  };

  const { translate } = useContext(LanguageContext);

  const [travelTimes, setTravelTimes] = useState<TravelTimes | null>(null);
  const [startAddress, setStartAddress] = useState<string>("");
  const [endAddress, setEndAddress] = useState<string>("");
  const [startLngLat, setStartLngLat] = useState<LngLatLike | null>(null);
  const [endLngLat, setEndLngLat] = useState<LngLatLike | null>(null);

  const [startPoint, setStartPoint] = useState<string | number | null>(null);
  const [endPoint, setEndPoint] = useState<string | number | null>(null);
  const [startID, setStartID] = useState<string | number | null>(null);
  const [endID, setEndID] = useState<string | number | null>(null);

  const routes = routesData as Routes;
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);

  useEffect(() => {
    if (mapContainer.current) {
      if (map.current) {
        map.current.remove();
      }
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: "mapbox://styles/ryancasburn/cm4kj5rsm00d001qle52w7psh",
        center: mapCenter,
        cooperativeGestures: isMobile,
        zoom: mapZoom,
      });

      map.current.addControl(
        new mapboxgl.NavigationControl({ showCompass: false }),
        "bottom-right"
      );

      const onStyleData = () => {
        if (!map.current) return;

        map.current.on("mousemove", "visum-zones-3ydgp8All", (e) => {
          if (!!e.features?.[0]) {
            map.current?.removeFeatureState({
              source: "composite",
              sourceLayer: "visum_zones-3ydgp8",
            });

            if (startID) {
              map.current?.setFeatureState(
                {
                  source: "composite",
                  sourceLayer: "visum_zones-3ydgp8",
                  id: startID,
                },
                { start: true }
              );
            }

            if (endID) {
              map.current?.setFeatureState(
                {
                  source: "composite",
                  sourceLayer: "visum_zones-3ydgp8",
                  id: endID,
                },
                { end: true }
              );
            }

            if (e.features[0].id !== undefined) {
              map.current?.setFeatureState(
                {
                  source: "composite",
                  sourceLayer: "visum_zones-3ydgp8",
                  id: e.features[0].id,
                },
                { hover: true }
              );
            }
          }
        });

        map.current.on("mouseleave", "visum-zones-3ydgp8All", () => {
          map.current?.removeFeatureState({
            source: "composite",
            sourceLayer: "visum_zones-3ydgp8",
          });

          if (startID) {
            map.current?.setFeatureState(
              {
                source: "composite",
                sourceLayer: "visum_zones-3ydgp8",
                id: startID,
              },
              { start: true }
            );
          }

          if (endID) {
            map.current?.setFeatureState(
              {
                source: "composite",
                sourceLayer: "visum_zones-3ydgp8",
                id: endID,
              },
              { end: true }
            );
          }
        });
      };

      map.current.on("load", () => {
        if (map.current?.isStyleLoaded()) {
          onStyleData();
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const removeMapPopups = () => {
    const existingPopups = document.getElementsByClassName("mapboxgl-popup");
    Array.from(existingPopups).forEach((popup) => popup.remove());
  };

  useEffect(() => {
    if (!map.current || !travelTimes) return;

    const routePopupHandler = (e: any) => {
      if (!map.current || !travelTimes) return;

      e.preventDefault();
      const routeState: RouteState | undefined = e.features?.[0]?.id
        ? (map.current?.getFeatureState({
          source: "composite",
          sourceLayer: "visum_links-5d3ilq",
          id: e.features[0]?.id,
        }) as RouteState) ?? undefined
        : undefined;

      if (routeState && getSelectedRoutes(routeState).length > 0) {
        removeMapPopups();

        // Create a DOM node for the React component
        const popupNode = document.createElement("div");
        // Create root and render the React component
        const root = createRoot(popupNode);
        root.render(
          <RoutePopup
            routeState={routeState}
            travelTimes={travelTimes ?? undefined}
          />
        );
        new mapboxgl.Popup({
          className: "z-50",
        })
          .setLngLat(e.lngLat)
          .setDOMContent(popupNode) // Use setDOMContent to attach the React component
          .addTo(map.current as mapboxgl.Map);
      }
    };

    // add popup to highlighted map routes
    map.current.on("click", "visum-links-5d3ilq", routePopupHandler);
    map.current.on("mouseenter", "visum-links-5d3ilq", routePopupHandler);
    map.current.on("mouseleave", "visum-links-5d3ilq", removeMapPopups);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [travelTimes]);

  const setZoneBoundsStyle = (property: "visibility", value: "none" | "visible") => {
    map.current?.setLayoutProperty("visum-zones-3ydgp8", property, value);
    map.current?.setLayoutProperty("visum-zones-3ydgp8All", property, value);
  };


  useEffect(() => {
    if (!map.current) return;

    /**
     * Handle clicks on map zones so users can set start and end points
     */
    const mapClickHandler = (e: any) => {
      const createMarkerElement = (text: string) => {
        const el = document.createElement("div");
        el.className =
          "px-3 py-2 bg-white border-2 border-black rounded-full text-sm font-medium shadow-md";
        el.textContent = text;
        return el;
      };

      const createMarker = (lnglat: LngLatLike, text: string) => {
        const marker = new mapboxgl.Marker({
          element: createMarkerElement(text),
          anchor: "center",
        })
          .setLngLat(lnglat)
          .addTo(map.current as mapboxgl.Map);

        return marker;
      };

      const calculateTravelTimes = (
        startPoint: string | number,
        clickedNO: string | number
      ) => {
        const startId = startPoint.toString() as MapRouteName;
        const endId = clickedNO.toString() as MapRouteName;
        const route = routes?.[startId]?.[endId] ?? {};
        const travelTimes: TravelTimes = {};
        for (const scenario in route) {
          travelTimes[scenario as MapRouteName] =
            route[scenario as MapRouteName]?.travel_time ?? undefined;

          const links =
            route[scenario as MapRouteName]?.links?.split(",") ?? [];
          for (const link of links) {
            const mapLinks =
              map.current?.querySourceFeatures("composite", {
                sourceLayer: "visum_links-5d3ilq",
                filter: ["==", ["to-string", ["get", "NO"]], link],
              }) ?? [];
            for (const mapLink of mapLinks) {
              map?.current?.setFeatureState(
                {
                  source: "composite",
                  sourceLayer: "visum_links-5d3ilq",
                  id: mapLink.id ?? 0,
                },
                { [`selected${scenario}`]: true }
              );
            }
          }
        }
        const numRoutes = Object.keys(travelTimes).length;
        if (numRoutes === 0) {
          setTimeout(() => resetRouteSelectionTool(), 0);
          alert(translate("noRoutesFound"));
          return;
        }
        if (numRoutes < MAP_ROUTE_NAMES.length) {
          alert(translate("notAllRoutesFound"));
        }
        setTravelTimes(travelTimes);
      };

      if (e.features?.[0]) {
        e.preventDefault();
        const clickedNO = e.features[0].properties.NO;
        const addTooltip = (tooltipText: string) => {
          // Create tooltip popup
          const tooltipNode = document.createElement("div");
          const root = createRoot(tooltipNode);
          root.render(<TooltipContent tooltipText={tooltipText} />);

          new mapboxgl.Popup({
            closeButton: false,
            className: "font-medium",
          })
            .setLngLat(e.lngLat)
            .setDOMContent(tooltipNode)
            .addTo(map.current as mapboxgl.Map);
        };
        if (!startPoint) {
          // these map zones can only be end points since the street is one-way
          const endPointOnlyIds = [157, 155, 140, 137, 136, 174];
          if (endPointOnlyIds.includes(clickedNO)) {
            return addTooltip(translate("canOnlyBeEndPoint"));
          }
          setStartID(e.features[0].id);
          setStartPoint(clickedNO);
          createMarker(e.lngLat, translate("mapStartMarker"));
          return;
        }
        if (!endPoint) {
          // these map zones can only be start points since the street is one-way
          const startPointOnlyIds = [177];
          if (startPointOnlyIds.includes(clickedNO)) {
            return addTooltip(translate("canOnlyBeStartPoint"));
          }
          setEndPoint(clickedNO);
          setEndID(e.features[0].id);
          createMarker(e.lngLat, translate("mapEndMarker"));
          calculateTravelTimes(startPoint, clickedNO);
          setZoneBoundsStyle("visibility", "none");
          return;
        }
        // else
        // points already set
      }
    };

    map.current.on("click", "visum-zones-3ydgp8All", mapClickHandler);
    const getSimulatedClickEvent = (lngLat: LngLatLike): Object | undefined => {
      if (!map.current) return;
      const features = map.current.queryRenderedFeatures(
        map.current.project(lngLat),
        {
          layers: ["visum-zones-3ydgp8All"],
        }
      );
      if (features.length === 0) {
        return;
      }
      return {
        lngLat,
        type: "click",
        target: map.current,
        originalEvent: new MouseEvent("click"),
        point: map.current.project(lngLat),
        features,
        preventDefault: () => { },
      };
    };
    if (!startPoint && startLngLat) {
      // Simulate a click event at the given lngLat coordinates
      const simulatedEvent = getSimulatedClickEvent(startLngLat);
      if (simulatedEvent) {
        mapClickHandler(simulatedEvent);
      } else {
        setStartAddress("");
        setStartLngLat(null);
        alert(translate("enterAddressInStudyArea"));
      }
    }
    if (!endPoint && endLngLat) {
      // Simulate a click event at the given lngLat coordinates
      const simulatedEvent = getSimulatedClickEvent(endLngLat);
      if (simulatedEvent) {
        mapClickHandler(simulatedEvent);
      } else {
        setEndAddress("");
        setEndLngLat(null);
        alert(translate("enterAddressInStudyArea"));
      }
    }
    return () => {
      if (map.current) {
        map.current.off("click", "visum-zones-3ydgp8All", mapClickHandler);
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endLngLat, startLngLat, startPoint, endPoint, routes]);

  const resetRouteSelectionTool = () => {
    // reset state
    setStartAddress("");
    setEndAddress("");
    setStartLngLat(null);
    setEndLngLat(null);

    setStartPoint(null);
    setEndPoint(null);
    setTravelTimes(null);
    setStartID(null);
    setEndID(null);

    // Remove all mapboxgl-marker elements from the DOM
    const mapMarkers = document.getElementsByClassName("mapboxgl-marker");
    Array.from(mapMarkers).forEach((marker) => marker.remove());

    // Reset map view to initial position
    map.current?.flyTo({ center: mapCenter, zoom: mapZoom, essential: true });

    map.current?.removeFeatureState({
      source: "composite",
      sourceLayer: "visum_zones-3ydgp8",
    });
    map.current?.removeFeatureState({
      source: "composite",
      sourceLayer: "visum_links-5d3ilq",
    });
    setZoneBoundsStyle("visibility", "visible");
    removeMapPopups();
  };

  return (
    <div>
      <AccessibleAddressInputs
        showResetButton={
          !!((startPoint && !startAddress) || (endPoint && !endAddress))
        }
        resetRouteSelectionTool={resetRouteSelectionTool}
        startAddress={startAddress}
        setStartAddress={setStartAddress}
        endAddress={endAddress}
        setEndAddress={setEndAddress}
        startLngLat={startLngLat}
        setStartLngLat={setStartLngLat}
        endLngLat={endLngLat}
        setEndLngLat={setEndLngLat}
      />
      <div className="grid grid-cols-12 gap-4 mt-8 mb-8">
        <div className="relative col-span-12 md:col-span-8">
          <div
            className={cn(isMobile ? "h-[500px]" : "h-[735px]")}
            ref={mapContainer}
          ></div>
          <div className="absolute top-2 w-full flex justify-center">
            <MapInstructionsCard
              endID={endID}
              resetMap={resetRouteSelectionTool}
              startID={startID}
            />
          </div>
        </div>
        <div className="col-span-12 md:col-span-4" id="travel-times-table">
          <TravelTimesTable travelTimes={travelTimes} />
        </div>
      </div>
    </div>
  );
}
